import {
  FaCheckCircle,
  FaClipboardCheck,
  FaFileInvoiceDollar,
  FaShieldAlt,
  FaTasks,
} from "react-icons/fa";
import Seo from "../../components/Seo";
import ServicePageTemplate from "../../components/ServicePageTemplate";
import { createBreadcrumbSchema } from "../../lib/seo";

const seo = {
  title: "Verification Of Benefits Support | Physicians Success Partners",
  description:
    "PSP RCM offers reliable benefits verification support to help healthcare providers verify insurance details, reduce denials, and improve cash flow.",
  keywords:
    "verification of benefits, eligibility verification, insurance benefit verification",
  schema: [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Verification of Benefits (VOB) Services",
      url: "https://psprcm.us/services/verification-of-benefits/",
      description:
        "PSP RCM offers reliable benefits verification support to help healthcare providers verify insurance details, reduce denials, and improve cash flow.",
      serviceType: "Insurance Eligibility Verification",
      provider: {
        "@type": "Organization",
        name: "Physicians Success Partners",
        url: "https://psprcm.us/",
        logo: "https://psprcm.us/logo/main-logo.webp",
        telephone: "+1 6155536787",
        email: "info@psprcm.us",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1616 Westgate Circle",
          addressLocality: "Brentwood",
          addressRegion: "TN",
          postalCode: "37027",
          addressCountry: "USA",
        },
      },
      areaServed: {
        "@type": "Country",
        name: "United States",
      },
      audience: {
        "@type": "MedicalAudience",
        audienceType: "Healthcare Providers",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceLocation: {
          "@type": "Place",
          address: { "@type": "PostalAddress", addressCountry: "USA" },
        },
        availableLanguage: ["English"],
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Verification of Benefits Solutions",
        itemListElement: [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Insurance Eligibility Verification" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Coverage & Benefits Validation" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Copay, Coinsurance & Deductible Analysis" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Prior Authorization Requirement Check" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Patient Financial Responsibility Estimation" } },
        ],
      },
    },
    createBreadcrumbSchema("/services/verification-of-benefits", "Verification Of Benefits Support"),
  ],
};

export default function VerificationBenefits() {
  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath="/services/verification-of-benefits"
        schema={seo.schema}
      />
      <ServicePageTemplate
        banner={{
        heading: "Verification of Benefits Service",
        paragraph:
          "Support cleaner intake and better financial clarity with thorough insurance benefit verification before services are delivered.",
        image: "/img/services-img/verification-of-benefits.webp",
      }}
      grid={{
        kicker: "Front-End Accuracy",
        heading: "Start Revenue Workflows With Stronger Coverage Information",
        description:
          "Benefit verification is one of the most important early steps in the revenue cycle. We help practices confirm eligibility, review plan details, and surface authorization or responsibility concerns before services are delivered, reducing confusion for both staff and patients.",
        metrics: [
          { icon: <FaClipboardCheck />, value: "Early", label: "Eligibility Validation", style: "metricPrimary" },
          { icon: <FaFileInvoiceDollar />, value: "Clearer", label: "Patient Responsibility Review", style: "metricAccent" },
          { icon: <FaTasks />, value: "Better", label: "Coverage Visibility", style: "metricLight" },
          { icon: <FaShieldAlt />, value: "100%", label: "Workflow Consistency", style: "metricDark" },
        ],
        panelEyebrow: "Benefit Clarity",
        panelHeading: "We help your team confirm the coverage details that support cleaner intake, smoother billing, and better financial conversations.",
        assurances: [
          { icon: <FaCheckCircle />, label: "Eligibility Checks" },
          { icon: <FaFileInvoiceDollar />, label: "Responsibility Review" },
          { icon: <FaTasks />, label: "Coverage Confirmation" },
        ],
      }}
      services={{
        heading: "Advantages of PSP Verification of Benefits Services",
        description:
          "Benefit verification is essential for avoiding billing surprises and improving front-end accuracy. We help practices confirm payer details, coverage information, and patient responsibility expectations before claims are submitted.",
        items: [
          { title: "Better Front-End Accuracy", text: "We verify insurance details early so scheduling and billing teams can work from clearer, more dependable information." },
          { title: "Fewer Coverage Surprises", text: "Our process helps identify plan details, authorization needs, and responsibility concerns before services move forward." },
          { title: "Stronger Patient Financial Clarity", text: "By validating benefits ahead of time, practices can set clearer expectations around eligibility and out-of-pocket costs." },
          { title: "Cleaner Downstream Billing", text: "Accurate benefit verification supports smoother claim preparation and helps reduce preventable rework later in the revenue cycle." },
        ],
      }}
      faq={{
        image: "/img/services-img/verification-of-benefits.webp",
        imageAlt: "Verification of benefits support",
        title: "Why Choose PSP for Verification of Benefits?",
        question: [
          "Why is verification of benefits important before service delivery?",
          "Can PSP help confirm eligibility and patient responsibility?",
          "Does this service support smoother claim submission later?",
        ],
        answer: [
          "Verifying benefits early helps reduce billing surprises, improve scheduling accuracy, and create a clearer financial experience for patients and staff.",
          "Yes. We help practices review eligibility, plan details, and expected patient responsibility with more consistency before services are rendered.",
          "Accurate front-end benefit verification helps build a cleaner foundation for billing and can reduce avoidable downstream issues.",
        ],
      }}
      specialtiesContent={{
        eyebrow: "Eligibility Support",
        heading: "Benefit verification workflows that strengthen intake accuracy across specialties.",
        description:
          "We help practices verify coverage details early so financial conversations and billing workflows begin with stronger information.",
        }}
      />
    </>
  );
}
